/* eslint-disable no-console */
import { useMemo } from 'react';
import { track } from '../../lib/util/tracking';
import { getStorePages } from '@osmosis/shared/products';
import { buildEcommerceItem } from '../buy/buyUtils/ecommerceTracking';

import { Deal, Plan } from '../../types/plans';
import { CURRENCY_CODE_SYMBOL_MAP } from '../../constants/plans';
import { IntlShape } from 'react-intl';
import { FetchContextProps } from '../../context/FetchProvider';

function pluralize(term: string, number: number) {
  return `${term}${number > 1 ? 's' : ''}`;
}

/**
 * PlanSelector and PlanSelectorNew Blocks.
 * Get title for PlanCard and PlanCardNew blocks.
 */
export const getTitle = (intervalLength: number, intl: IntlShape) => {
  if (intervalLength === 1) {
    return intl.formatMessage({
      id: 'plans.title.monthly',
      defaultMessage: '1 month',
    });
  }

  if (intervalLength % 12 === 0) {
    const years = intervalLength / 12;

    return intl.formatMessage(
      {
        id: 'plans.title.years',
        defaultMessage: `{years} {years, plural, one {year} other {years}}`,
      },
      { years },
    );
  }

  return intl.formatMessage(
    {
      id: 'plans.title.months',
      defaultMessage: `{intervalLength} {intervalLength, plural, one {month} other {months}}`,
    },
    { intervalLength },
  );
};

/**
 * Returns the subtitle for a given index.
 * @param {number} index - The index of the subtitle.
 * @returns {} The subtitle.
 */
export const getSubtitle = (index: number, intl: IntlShape) => {
  return (
    [
      intl.formatMessage({
        id: 'plans.subtitle.best-value',
        defaultMessage: 'Best value',
      }),
      intl.formatMessage({
        id: 'plans.subtitle.most-popular',
        defaultMessage: 'Most popular',
      }),
    ][index] || ''
  );
};

/**
 * PlanSelector and PlanSelectorNew Blocks.
 * Get renewal title for PlanCard and PlanCardNew blocks.
 */
export const getRenewalTitle = (intervalLength: number) => {
  return (
    {
      24: {
        id: 'plans.bill_24',
        defaultMessage: 'Billed every 2 years',
      },
      12: {
        id: 'plans.bill_12',
        defaultMessage: 'Billed annually',
      },
      1: {
        id: 'plans.bill_monthly',
        defaultMessage: 'Billed monthly',
      },
    }[intervalLength] || {
      id: 'plans.bill_month',
      defaultMessage: `Billed every ${intervalLength} ${pluralize(
        'month',
        intervalLength,
      )}`,
    }
  );
};

/**
 * Returns the currency symbol for a given currency code.
 * @param {string} currency - The currency code.
 * @returns {string} The currency symbol.
 */
export const getCurrencySymbol = (currency: string) => {
  // Handle undefined/null currency from Recurly API
  if (!currency) return '$';

  return (
    CURRENCY_CODE_SYMBOL_MAP[
      currency as keyof typeof CURRENCY_CODE_SYMBOL_MAP
    ] || currency
  );
};

const promiseTimeout = (promise: Promise<void | void[]>, timeout: number) =>
  Promise.race([
    promise,
    new Promise<void>((resolve) => setTimeout(() => resolve(), timeout)),
  ]);

/**
 * Track plan selection (fires when user clicks a plan to go to checkout).
 * Uses buildEcommerceItem for consistency across all GA4 events.
 */
export const trackPlanSelection = async (plan: Plan, deal?: Deal) => {
  const getPlanVersion = (code: string) => code.split('-')[2] || 'Default';
  const getProduct = (code: string) => code?.split('-')[0] || '';

  // Track to Keen
  const selectedPlan = {
    inventoryID: plan.hash,
    product: getStorePages().find(
      (page) => page.key === plan.product.toUpperCase(),
    )?.storeName,
    product_code: getProduct(plan.code),
    plan_code: plan.code,
    plan_version: getPlanVersion(plan.code),
    months: plan.intervalLength,
    amount: plan.currency.unitAmount,
    unit_amount: plan.currency.unitAmount,
    currency: plan.currency.currency,
    deal_name: deal?.url,
  };

  await promiseTimeout(track('click_plan', selectedPlan, ['keen']), 500);

  // Track to GTM using buildEcommerceItem for consistency
  const selectedPlanGTM = {
    click: {
      actionField: {
        list: 'Catalog Page',
        items: [buildEcommerceItem(plan)],
      },
    },
  };
  await promiseTimeout(track('select_item', selectedPlanGTM, ['gtm']), 500);
};

/**
 * Track view_item GTM event for plans page.
 * Uses buildEcommerceItem for consistency.
 */
export const trackPlansView = async (plans: Plan[]) => {
  if (!plans?.length) return;

  // Use buildEcommerceItem for consistency
  const formattedPlans = plans.map((plan: Plan) => buildEcommerceItem(plan));

  track(
    'view_item',
    {
      ecommerce: {
        currency: plans[0].currency.currency,
        value: plans[0].currency.USDUnitAmount || plans[0].currency.unitAmount,
        detail: {
          items: formattedPlans,
        },
      },
    },
    ['gtm'],
  );
};

/**
 * Fetch plans from API.
 */
export const getPlans = async ({
  planLengths,
  program,
  productID,
  fetch,
  planVersion,
  dealType,
  dealCode,
}: {
  planLengths: string[];
  program: string;
  productID: string;
  fetch: FetchContextProps;
  planVersion?: string;
  dealType?: string;
  dealCode?: string;
}) => {
  const url = new URL('http://localhost:3000/v0/plans');
  url.searchParams.append('program', program);

  // Always include both productIDs[] and planLengths[] if present
  if (productID) {
    url.searchParams.append('productIDs[]', productID);
  }

  if (planLengths?.length) {
    planLengths.forEach((planLength: string) =>
      url.searchParams.append('planLengths[]', planLength),
    );
  }

  if (planVersion) url.searchParams.append('planVersion', planVersion);
  if (dealType) url.searchParams.append('dealType', dealType);
  if (dealCode) url.searchParams.append('dealCode', dealCode);

  const fullUrl = url.pathname + url.search;

  try {
    const response = await fetch.get<{ plans: Plan[] }>(fullUrl);
    const { plans } = response.data;
    return plans || [];
  } catch (error) {
    console.error('[getPlans] Error fetching plans:', error);
    return [];
  }
};

/**
 * Check if plan is allowed for the deal.
 */
export function isAllowedPlan(plan: Plan, deal?: Deal): boolean {
  const allowedPlans = deal?.data?.allowedPlans;
  if (!allowedPlans) return true;
  return allowedPlans.includes(String(plan.intervalLength));
}

/**
 * Calculate reduced price based on deal discount.
 */
export function useReducedPrice(isDeal: boolean, plan?: Plan, deal?: Deal) {
  return useMemo(() => {
    let result: number;
    const allowed = plan && deal ? isAllowedPlan(plan, deal) : true;

    if (isDeal && allowed) {
      const origUnitAmount = parseInt(
        String(
          plan?.currency?.origUnitAmount ?? plan?.currency?.unitAmount ?? '0',
        ),
        10,
      );
      const discountAmount = parseInt(String(deal?.discountAmount ?? '0'), 10);

      const computed =
        deal?.discountType === 'percent'
          ? origUnitAmount - (origUnitAmount * discountAmount) / 100
          : origUnitAmount - discountAmount;
      result = computed;
    } else {
      result = plan?.currency?.unitAmount ?? 0;
    }
    return Math.round(result);
  }, [isDeal, plan, deal]);
}

/**
 * Format price for display (show decimals only if needed).
 */
export function formatPrice(value: number | string): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return num % 1 === 0 ? num.toString() : num.toFixed(2);
}

/**
 * Generate buy page href with plan, deal, and upsell parameters.
 */
export function getPlanHref(
  plan: Plan,
  deal?: Deal,
  upsellPlan?: Plan | null,
): string {
  const allowed = isAllowedPlan(plan, deal);
  const dealURL = deal && allowed ? `&deal=${deal.url}` : '';
  return `/buy?plan=${plan.code}${dealURL}${
    upsellPlan?.code ? `&upsellPlan=${upsellPlan.code}` : ''
  }`;
}

/**
 * Get monthly price breakdown and text for a plan.
 */
export const getMonthlyPriceData = (plan: Plan, intl: IntlShape) => {
  const { formatMessage } = intl;
  const textMap: { [key: number]: string } = {
    3: formatMessage({
      id: 'plans.every_3_months',
      defaultMessage: 'every 3 months',
    }),
    1: formatMessage({
      id: 'plans.every_month',
      defaultMessage: 'every month',
    }),
    6: formatMessage({
      id: 'plans.every_6_months',
      defaultMessage: 'every 6 months',
    }),
    9: formatMessage({
      id: 'plans.every_9_months',
      defaultMessage: 'every 9 months',
    }),
    12: formatMessage({
      id: 'plans.annually',
      defaultMessage: 'annually',
    }),
    24: formatMessage({
      id: 'plans.every_2_years',
      defaultMessage: 'every 2 years',
    }),
  };

  const defaultText = formatMessage({
    id: 'plans.annually',
    defaultMessage: 'annually',
  });

  const text = plan.autoRenew
    ? textMap[Number(plan.intervalLength)] || defaultText
    : '';

  // Annual price calculation
  const annualPrice = Math.floor(plan.currency.unitAmount);

  // Monthly price calculation (prorated from annual)
  const monthlyPrice = Math.floor(
    plan.currency.unitAmount / plan.intervalLength,
  );
  // Daily price calculation (prorated from monthly)
  const days = plan.intervalLength * 30.44;
  const dailyPrice = (plan.currency.unitAmount / days).toFixed(2);
  const currencySymbol = getCurrencySymbol(plan.currency.currency);

  return {
    text,
    annualPrice,
    monthlyPrice,
    dailyPrice,
    currencySymbol,
  };
};
